import { useEffect, useRef, useState } from 'react';
import Badge from 'react-bootstrap/Badge';
import Dropdown from 'react-bootstrap/Dropdown';
import DropdownItem from 'react-bootstrap/DropdownItem';
import FormControl from 'react-bootstrap/FormControl';
import CheckInLogoNew from '../../logo/gte_logo.png';
import { useAuth } from '../../context/AuthContext';
import { getApplicationGuideInfoLink } from '../../services/apiClient';
import { ResponseToast } from '../common/ResponseToast';
import type { DelegatedUser, SelectedDate } from '../../types';

/**
 * Port of `components/navbar.jsx`.
 *
 * Two of its values were hardcoded absolute URLs in the original and now come from the
 * runtime config, seeded with the same literals:
 *  - the "Home" menu item's Oracle Fusion URL (navbar.jsx:192-193)
 *  - nothing else here; the application-guide link was already fetched from the source
 *    service (INFO_PAGE), and still is.
 *
 * Dropped: the unused `CheckInLogo` import (the app renders `gte_logo.png`), and the
 * `getApplicationGuideInfor` import from utils/services, which is not defined there and
 * resolved to `undefined` (navbar.jsx:9).
 */

interface DelegatedOption {
  delegatedUserGteEmployeeNo: number;
  delegatedUserName: string;
}

export function NavBar({
  alertData,
  handleSave,
  updateStateOfClickedAlert,
  selectedDate,
}: {
  alertData: { alertSummary?: number } | null;
  /** Saves the current week before navigating away — the original's `handleSave`. */
  handleSave: () => void;
  updateStateOfClickedAlert: (value: boolean) => void;
  selectedDate: SelectedDate | null;
}): JSX.Element {
  const {
    config,
    loggedUserDetails,
    delegatedUsers,
    loggedUserGteEmployeeNo,
    operationalUserGteEmployeeNo,
    selectDelegatedUser,
  } = useAuth();

  const [searchValue, setSearchValue] = useState('');
  const [selectedOption, setSelectedOption] = useState<DelegatedOption | null>(null);
  const [response, setResponse] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [applicationGuide, setApplicationGuide] = useState<{ status: boolean; link: string }>({
    status: false,
    link: '',
  });

  /**
   * The original's 1s debounce, implemented with a `lastRequestId` guard so a rapid
   * remount could not let an earlier response overwrite a later one. Kept, with the
   * timeout cleared on unmount — which the original did not do.
   *
   * Waits for login — firing on mount raced the Okta token exchange and sent the call
   * with no bearer token (400), leaving the guide button disabled for the session.
   */
  const isLoggedIn = loggedUserDetails !== null;
  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      getApplicationGuideInfoLink()
        .then((res) => {
          if (cancelled) return;
          const first = (res as unknown as Array<{ configValue?: string }> | undefined)?.[0];
          setApplicationGuide({ status: true, link: first?.configValue ?? '' });
        })
        // The original had no catch here, so a failure left the button disabled. Same
        // outcome, without the unhandled rejection.
        .catch(() => undefined);
    }, 1000);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [isLoggedIn]);

  /**
   * navbar.jsx:36-49 — announce a delegated-user switch, but only once a selection has
   * actually been made (so the initial load is silent).
   */
  const lastAnnouncedRef = useRef<number>(operationalUserGteEmployeeNo);
  useEffect(() => {
    if (lastAnnouncedRef.current === operationalUserGteEmployeeNo) return;
    lastAnnouncedRef.current = operationalUserGteEmployeeNo;
    if (selectedOption !== null) {
      setResponse(true);
      setResponseMessage(`User switched to ${selectedOption.delegatedUserName}`);
    }
  }, [operationalUserGteEmployeeNo, selectedOption]);

  /** navbar.jsx:50-52 — clear the search box whenever the week changes. */
  useEffect(() => {
    setSearchValue('');
  }, [selectedDate]);

  const handleOptionSelect = (option: DelegatedOption): void => {
    setSelectedOption(option);
    setSearchValue('');
    selectDelegatedUser(Number(option.delegatedUserGteEmployeeNo), loggedUserGteEmployeeNo);
  };

  // App.js:236-241 — alphabetical, recomputed on every render.
  const sortedDelegatedUsers = [...((delegatedUsers as DelegatedUser[]) ?? [])].sort((a, b) =>
    a.delegatedUserName.localeCompare(b.delegatedUserName),
  );

  // `startsWith`, not `includes` — the original switched to prefix matching and left the
  // substring version commented out beside it (navbar.jsx:107).
  const filteredOptions = sortedDelegatedUsers.filter((option) =>
    String(option.delegatedUserName).toLowerCase().startsWith(searchValue.toLowerCase()),
  );

  const renderLogInAndLogoutOption = (): JSX.Element => {
    if (loggedUserDetails === null) {
      // The original renders an empty <li> here; the log-in button inside it is
      // commented out, because an unauthenticated user never reaches this screen.
      return (
        <>
          <li className="row"></li>
        </>
      );
    }

    return (
      <>
        <div className="row">
          <div
            className="col-2 notifications"
            title={`${alertData?.alertSummary} notifications`}
            {...{ alt: `${alertData?.alertSummary} notifications` }}
          >
            <button
              title={`alerts : ${alertData?.alertSummary} notifications`}
              onClick={() => updateStateOfClickedAlert(true)}
            >
              <Badge bg="secondary">{alertData?.alertSummary}</Badge>
            </button>
          </div>
          <div className="col-8 float-end user-dropdown">
            <Dropdown>
              <Dropdown.Toggle
                variant="light"
                id="dropdown-basic"
                title={
                  selectedOption ? selectedOption.delegatedUserName : loggedUserDetails.displayName
                }
              >
                {selectedOption ? selectedOption.delegatedUserName : loggedUserDetails.displayName}
              </Dropdown.Toggle>

              <Dropdown.Menu style={{ maxHeight: '250px', overflowY: 'scroll' }}>
                {/* Switch back to yourself. */}
                {/* `value` on a Dropdown.Item renders an <a value="…">, which is not
                    valid HTML and which nothing reads — but React passes it through, so
                    it is in the original's DOM. Spread past the typings to keep it. */}
                <DropdownItem
                  key={loggedUserGteEmployeeNo}
                  title={loggedUserDetails?.displayName}
                  {...{ value: loggedUserGteEmployeeNo }}
                  onClick={() =>
                    handleOptionSelect({
                      delegatedUserGteEmployeeNo: loggedUserGteEmployeeNo,
                      delegatedUserName: loggedUserDetails?.displayName,
                    })
                  }
                >
                  {loggedUserDetails?.displayName}
                </DropdownItem>
                <DropdownItem
                  title="Home"
                  onClick={() => {
                    // Saves first — navigating away otherwise loses the week's edits.
                    handleSave();
                    window.location.href = config?.oracleFusionHomeUrl ?? '';
                  }}
                >
                  Home
                </DropdownItem>
                <FormControl
                  className="user-list"
                  type="text"
                  placeholder="Search..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                />
                {filteredOptions?.map((option) => (
                  <Dropdown.Item
                    key={option.delegatedUserGteEmployeeNo as number}
                    title={option.delegatedUserName}
                    onClick={() => handleOptionSelect(option as unknown as DelegatedOption)}
                  >
                    {option.delegatedUserName}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <div className="col-2 application_guide">
            <button
              disabled={!applicationGuide?.status}
              onClick={(e) => {
                handleSave();
                e.preventDefault();
                window.open(applicationGuide.link, '_blank');
              }}
              tabIndex={0}
              title="Checkin Application Guide"
              {...{ alt: 'Checkin Application Guide' }}
            >
              <Badge bg="secondary"></Badge>
            </button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="ch-header">
      <div tabIndex={0} className="app-logo" title="CheckIn" {...{ alt: 'CheckIn Logo' }}>
        <img src={CheckInLogoNew} alt="CheckIn" />
      </div>
      <div className="logged-user-info">
        <ul className="logged-users">{renderLogInAndLogoutOption()}</ul>
      </div>
      <section>
        {response && (
          <ResponseToast
            responseMessage={responseMessage}
            hide={() => {
              setResponse(false);
            }}
          />
        )}
      </section>
    </div>
  );
}
